// Client for the Worker's paid relay API (apps/worker/src/relay.ts,
// docs/adr/0013), and the pass kept in this browser. The pass token is a
// bearer credential: it goes in request bodies, never a URL, and is stored
// only in this browser's localStorage.

import type { CheckoutStatus, CreatePassCheckoutRequest, CreateRelayGrantRequest, PassCheckout, PassStatus, RelayGrant, SettleRelayGrantRequest } from '@sss/types';
import { API_BASE_URL } from './config';
import { SecretsApiError, json, request } from './secretsApi';

export const PASS_STORAGE_KEY = 'sss.pass';
/** A pass being bought: kept apart until Stripe confirms the payment, so it never looks usable before then. */
export const PENDING_PASS_STORAGE_KEY = 'sss.passPending';
export const PASS_PATTERN = /^sss_pass_[A-Za-z0-9_-]{43}$/;

/** Why the relay can't be used, from the Worker's status codes. */
export type RelayProblem = 'passPending' | 'passNotFound' | 'passExpired' | 'allowanceExceeded' | 'tooManyGrants' | 'relayUnavailable' | 'rateLimited' | 'network' | 'other';

export const relayProblem = (err: unknown): RelayProblem => {
  if (!(err instanceof SecretsApiError)) return 'other';
  if (err.type === 'network' || err.type === 'rateLimited' || err.type === 'passPending') return err.type;
  const byStatus: Record<number, RelayProblem> = { 402: 'allowanceExceeded', 404: 'passNotFound', 409: 'tooManyGrants', 410: 'passExpired', 503: 'relayUnavailable' };
  return byStatus[err.status] ?? 'other';
};

export const getPassStatus = (pass: string, base?: string) => request<PassStatus>('/v1/passes/status', json('POST', { pass }), base);

/** Start buying a pass: Stripe's Checkout page, and the token the pass will be once paid. */
export const createPassCheckout = (input: CreatePassCheckoutRequest, base?: string) => request<PassCheckout>('/v1/checkout/passes', json('POST', input), base);

/** Where a purchase has got to: a top-up's page polls this, since its pass already works. */
export const getCheckoutStatus = (checkout: string, base?: string) => request<CheckoutStatus>(`/v1/checkout/passes/${encodeURIComponent(checkout)}`, {}, base);

export const createRelayGrant = (input: CreateRelayGrantRequest, base?: string) => request<RelayGrant>('/v1/relay/grants', json('POST', input), base);

/**
 * Charge what was relayed and release the rest. `keepalive` lets it finish
 * when the tab is closing; a grant that never settles is settled by the
 * Worker when its credential expires.
 */
export const settleRelayGrant = (id: string, input: SettleRelayGrantRequest, base = API_BASE_URL): Promise<void> =>
  fetch(`${base}/v1/relay/grants/${encodeURIComponent(id)}/settle`, { ...json('POST', input), keepalive: true, referrerPolicy: 'no-referrer', cache: 'no-store' }).then(
    () => undefined,
    () => undefined,
  );

const storage = (): Storage | undefined => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const load = (key: string): string | null => {
  try {
    const pass = storage()?.getItem(key) ?? null;
    return pass && PASS_PATTERN.test(pass) ? pass : null;
  } catch {
    return null;
  }
};

const save = (key: string, pass: string): void => {
  try {
    storage()?.setItem(key, pass);
  } catch {
    // Private windows can refuse storage; the pass still works for this page.
  }
};

const forget = (key: string): void => {
  try {
    storage()?.removeItem(key);
  } catch {
    // Nothing stored.
  }
};

/**
 * Fired on this window when the saved pass changes. Other tabs hear the
 * storage event instead, which a tab never gets for its own writes.
 */
export const PASS_CHANGE_EVENT = 'sss:pass-change';

const announce = () => window.dispatchEvent(new Event(PASS_CHANGE_EVENT));

/** The pass kept in this browser, if any. */
export const loadPass = (): string | null => load(PASS_STORAGE_KEY);
export const savePass = (pass: string): void => {
  save(PASS_STORAGE_KEY, pass);
  announce();
};
export const forgetPass = (): void => {
  forget(PASS_STORAGE_KEY);
  announce();
};

/** The pass this browser is buying, if any. */
export const loadPendingPass = (): string | null => load(PENDING_PASS_STORAGE_KEY);
export const savePendingPass = (pass: string): void => save(PENDING_PASS_STORAGE_KEY, pass);
export const forgetPendingPass = (): void => forget(PENDING_PASS_STORAGE_KEY);

/** A top-up being bought: the checkout to wait on (the pending pass is the one being topped up). */
const TOP_UP_STORAGE_KEY = 'sss.passTopUp';
const CHECKOUT_PATTERN = /^[0-9a-f-]{36}$/;

export const loadPendingTopUp = (): string | null => {
  try {
    const value = storage()?.getItem(TOP_UP_STORAGE_KEY) ?? null;
    return value && CHECKOUT_PATTERN.test(value) ? value : null;
  } catch {
    return null;
  }
};
export const savePendingTopUp = (checkout: string): void => {
  try {
    storage()?.setItem(TOP_UP_STORAGE_KEY, checkout);
  } catch {
    // Without storage the bought page waits on the pass instead.
  }
};
export const forgetPendingTopUp = (): void => forget(TOP_UP_STORAGE_KEY);
